import { useState, type FormEvent } from 'react';

import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { Dialog } from '@/components/Dialog/Dialog';
import { FileDropzone } from '@/components/FileDropzone/FileDropzone';
import { TextField } from '@/components/TextField/TextField';
import { isValidKey, keyFromName, titleFromFilename } from '@/lib/lenders';
import type { UploadProgress } from '@/lib/upload';

import styles from './DocumentForm.module.css';

export type DocumentFormMode = 'add' | 'replace' | 'retitle';

export interface DocumentDraft {
  file: File | null;
  title: string;
  key: string;
}

export interface DocumentFormProps {
  mode: DocumentFormMode;
  lenderName: string;
  /** The document's current title, for `replace` and `retitle`. */
  currentTitle?: string;
  busy?: boolean;
  progress?: UploadProgress | null;
  error?: string | null;
  onSubmit: (draft: DocumentDraft) => void;
  onCancel: () => void;
}

const COPY = {
  add: {
    title: 'Add a document',
    description: 'Clients are given it straight away, at the end of this lender’s list.',
    submit: 'Upload and publish',
  },
  replace: {
    title: 'Upload a new version',
    description: 'Clients are given the new file from now on. The old one stays in the history.',
    submit: 'Upload new version',
  },
  retitle: {
    title: 'Rename document',
    description: 'A new title is saved as a new version, so the history shows what clients saw.',
    submit: 'Save new title',
  },
} as const;

const ACCEPT = 'application/pdf,.pdf';

/** Add a lender document, upload a new version of one, or correct its title. */
export function DocumentForm({
  mode,
  lenderName,
  currentTitle = '',
  busy = false,
  progress = null,
  error = null,
  onSubmit,
  onCancel,
}: DocumentFormProps) {
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState(mode === 'add' ? '' : currentTitle);
  const [titleEdited, setTitleEdited] = useState(mode !== 'add');
  const [key, setKey] = useState('');
  const [keyEdited, setKeyEdited] = useState(false);
  const [touched, setTouched] = useState(false);

  const needsFile = mode !== 'retitle';
  const effectiveKey = keyEdited ? key : keyFromName(title);
  const fileError = touched && needsFile && !file;
  const titleError = touched && !title.trim() ? 'Give the document a title.' : undefined;
  const keyError =
    mode === 'add' && touched && !isValidKey(effectiveKey)
      ? 'Use lowercase letters, numbers and underscores, starting with a letter.'
      : undefined;
  const unchanged = mode === 'retitle' && title.trim() === currentTitle.trim();

  const chooseFile = (chosen: File | null) => {
    setFile(chosen);
    if (chosen && !titleEdited) {
      setTitle(titleFromFilename(chosen.name));
    }
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (
      (needsFile && !file) ||
      !title.trim() ||
      unchanged ||
      (mode === 'add' && !isValidKey(effectiveKey))
    ) {
      return;
    }
    onSubmit({ file, title: title.trim(), key: effectiveKey });
  };

  const copy = COPY[mode];
  const formId = `document-form-${mode}`;
  return (
    <Dialog
      actions={
        <>
          <Button disabled={busy} onClick={onCancel} variant="ghost">
            Cancel
          </Button>
          <Button disabled={unchanged} form={formId} loading={busy} type="submit">
            {copy.submit}
          </Button>
        </>
      }
      description={copy.description}
      dismissable={!busy}
      eyebrow={<span className={styles.lender}>{lenderName}</span>}
      onClose={onCancel}
      title={copy.title}
    >
      <form id={formId} className={styles.form} noValidate onSubmit={submit}>
        {needsFile ? (
          <div>
            <FileDropzone
              accept={ACCEPT}
              disabled={busy}
              file={file}
              hint="PDF documents only"
              label="File"
              onChange={chooseFile}
              progress={progress}
            />
            {fileError ? (
              <p className={styles.fileError} role="alert">
                Choose a file to upload.
              </p>
            ) : null}
          </div>
        ) : null}
        <TextField
          autoComplete="off"
          data-autofocus={mode === 'retitle' ? true : undefined}
          disabled={busy}
          error={titleError}
          hint={
            mode === 'add'
              ? 'The document’s official name, exactly as the lender writes it. Filled in from the file name.'
              : 'The document’s official name, exactly as the lender writes it.'
          }
          label="Title"
          onChange={(event) => {
            setTitleEdited(true);
            setTitle(event.target.value);
          }}
          value={title}
        />
        {mode === 'add' ? (
          <TextField
            autoComplete="off"
            disabled={busy}
            error={keyError}
            hint="How the LendWell app recognises this document. Filled in from the title, and it can't be changed later."
            label="Reference"
            onChange={(event) => {
              setKeyEdited(true);
              setKey(event.target.value);
            }}
            value={effectiveKey}
          />
        ) : null}
        {error ? (
          <Callout tone="danger" title="That didn't work">
            {error}
          </Callout>
        ) : null}
      </form>
    </Dialog>
  );
}
